import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import React from 'react'

import { ActivityTypeEnum, ClientActivity } from '@/api/clients/get-client-activities'
import { TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { formatDateOnly, formatDatePartsInTimezone, formatTimeInTimezone } from '@/utils/date'

import { makeActivityTypeLabels } from './recent-activities.constants'
import { ActivityType } from './recent-activities.types'

const getReservationDescription = (
  activity: ClientActivity,
  timezone: string,
  t: TFunction<['clients']>,
): string => {
  if (!activity.date) return ''
  const amenity = activity.amenityId?.name ?? ''

  if (activity.amenityId?.multiDayBooking && activity.endDate) {
    const start = formatDatePartsInTimezone(activity.date, timezone)
    const end = formatDatePartsInTimezone(activity.endDate, timezone)
    const interpolation = {
      amenity,
      endDay: end.day,
      endMonth: end.month,
      startDay: start.day,
      startMonth: start.month,
    }
    if (activity.approved) {
      return t('clients:recentActivities.descriptions.reservation.confirmedMultiDay', interpolation)
    }
    if (activity.cancelled) {
      return t('clients:recentActivities.descriptions.reservation.canceledMultiDay', interpolation)
    }
    return t('clients:recentActivities.descriptions.reservation.requestedMultiDay', interpolation)
  }

  if (activity.startTime == null || activity.endTime == null) return ''
  const startMs = activity.date + activity.startTime * 60 * 1000
  const endMs = activity.date + activity.endTime * 60 * 1000
  const startTime = formatTimeInTimezone(startMs, timezone)
  const endTime = formatTimeInTimezone(endMs, timezone)
  const { day, month } = formatDatePartsInTimezone(activity.date, timezone)
  const interpolation = { amenity, day, endTime, month, startTime }
  if (activity.approved) {
    return t('clients:recentActivities.descriptions.reservation.confirmed', interpolation)
  }
  if (activity.cancelled) {
    return t('clients:recentActivities.descriptions.reservation.canceled', interpolation)
  }
  return t('clients:recentActivities.descriptions.reservation.requested', interpolation)
}

const getGuestDescription = (
  activity: ClientActivity,
  timezone: string,
  t: TFunction<['clients']>,
): string => {
  if (!activity.startTime) return ''
  const time = formatTimeInTimezone(activity.startTime, timezone)
  const name = activity.name ?? t('clients:recentActivities.descriptions.guest.fallbackName')
  return t('clients:recentActivities.descriptions.guest.checkedIn', { name, time })
}

const getPackageDescription = (
  activity: ClientActivity,
  timezone: string,
  t: TFunction<['clients']>,
): string => {
  const ts = activity.updatedAt ?? activity.createdAt
  const { day, month, time } = formatDatePartsInTimezone(ts, timezone)
  const interpolation = { day, month, time }
  return activity.signature
    ? t('clients:recentActivities.descriptions.package.pickedUp', interpolation)
    : t('clients:recentActivities.descriptions.package.arrived', interpolation)
}

const getMaintenanceDescription = (
  activity: ClientActivity,
  timezone: string,
  t: TFunction<['clients']>,
): string => {
  const ts = activity.updatedAt ?? activity.createdAt
  const { day, month, time } = formatDatePartsInTimezone(ts, timezone)
  return t('clients:recentActivities.descriptions.maintenance', {
    day,
    month,
    status: activity.status ?? '—',
    time,
  })
}

const getNoteDescription = (activity: ClientActivity): string => activity.name ?? ''

const getPostDescription = (
  activity: ClientActivity,
  timezone: string,
  t: TFunction<['clients']>,
): string => {
  const ts = activity.updatedAt ?? activity.createdAt
  const { day, month, time } = formatDatePartsInTimezone(ts, timezone)
  return t('clients:recentActivities.descriptions.post', { day, month, time })
}

export const getActivityDescription = (
  activity: ClientActivity,
  timezone: string,
  t: TFunction<['clients']>,
): string => {
  switch (activity.historyType) {
    case ActivityTypeEnum.enum.reservation:
      return getReservationDescription(activity, timezone, t)
    case ActivityTypeEnum.enum.guest:
      return getGuestDescription(activity, timezone, t)
    case ActivityTypeEnum.enum.package:
      return getPackageDescription(activity, timezone, t)
    case ActivityTypeEnum.enum.maintenance:
      return getMaintenanceDescription(activity, timezone, t)
    case ActivityTypeEnum.enum.note:
      return getNoteDescription(activity)
    case ActivityTypeEnum.enum.post:
      return getPostDescription(activity, timezone, t)
  }
}

export const filterActivities = (
  activities: ClientActivity[],
  categories: ActivityType[],
  dateRange: [Date | null, Date | null],
): ClientActivity[] =>
  activities.filter((activity) => {
    if (categories.length > 0 && !categories.includes(activity.historyType)) return false

    const ts = activity.updatedAt ?? activity.createdAt
    const date = new Date(ts)
    if (dateRange[0] && date < dateRange[0]) return false
    if (dateRange[1]) {
      const endOfDay = new Date(dateRange[1])
      endOfDay.setHours(23, 59, 59, 999)
      if (date > endOfDay) return false
    }

    return true
  })

export const getColumns = (
  timezone: string,
  t: TFunction<['clients']>,
): TableColumn<ClientActivity>[] => {
  const activityTypeLabels = makeActivityTypeLabels(t)

  return [
    {
      id: 'category',
      render: (item) => activityTypeLabels[item.historyType] ?? item.historyType,
      title: t('clients:recentActivities.columns.category'),
      width: '16rem',
    },
    {
      id: 'description',
      render: (item) => getActivityDescription(item, timezone, t),
      title: t('clients:recentActivities.columns.description'),
    },
    {
      id: 'dateTime',
      render: (item) => {
        const zonedDate = toZonedTime(new Date(item.updatedAt ?? item.createdAt), timezone)
        return React.createElement(
          'div',
          null,
          React.createElement(Typography, {
            children: formatDateOnly(zonedDate),
            color: 'black500',
            size: 'text-small',
            weight: 'medium',
          }),
          React.createElement(Typography, {
            children: formatTimeInTimezone(item.updatedAt ?? item.createdAt, timezone),
            color: 'black500',
            size: 'text-small',
            weight: 'medium',
          }),
        )
      },
      title: t('clients:recentActivities.columns.dateTime'),
      width: '20rem',
    },
  ]
}
